import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { alertSlice } from "src/store/alert/alert.slice";
import { selector } from "src/react-query/select";
import { is } from "src/util/is";
import { useSelectedTeam } from "./useSelectedTeam";
import { useParams } from "react-router-dom";
import { useEffect, useCallback, useMemo } from "react";

const updateChargerList = (chargers) => {
  const updatedChargerList = chargers.reduce((updatedChargerList, charger) => {
    let status = "Inactive";
    if (charger.status === "A") {
      status = "Active";
    }

    const updatedCharger = { ...charger, status };
    updatedChargerList = [...updatedChargerList, updatedCharger];
    return updatedChargerList;
  }, []);
  return updatedChargerList;
};

export const useList = (queryKey, enabled, ...args) => {
  const { teamId } = useSelectedTeam();
  const enabledFlag =
    typeof enabled === "boolean" ? enabled : !is.empty(teamId);
  const { data, isFetching, refetch, error, isLoading } = useQuery(
    !args.length
      ? queryKeys[queryKey](teamId)
      : queryKeys[queryKey](teamId, ...args),
    fetchWithError,
    {
      enabled: enabledFlag,
      select: selector,
    }
  );
  if (queryKey === "chargers" && !is.empty(data?.chargers)) {
    const updatedChargerList = updateChargerList(data?.chargers);
    return { data: updatedChargerList, isFetching };
  }
  return { data, isFetching, refetch, hasError: error, isLoading: false };
};

export const useListWithCompanyId = (queryKey, enabled, teamId, ...args) => {
  const enabledFlag =
    typeof enabled === "boolean" ? enabled : !is.empty(teamId);
  const { data, isFetching, refetch, error, isLoading } = useQuery(
    !args.length
      ? queryKeys[queryKey](teamId)
      : queryKeys[queryKey](teamId, args),
    fetchWithError,
    {
      enabled: enabledFlag,
      select: selector,
    }
  );
  return { data, isFetching, refetch, hasError: error, isLoading: false };
};

export const useGetDetail = (queryKey, { id } = {}) => {
  const { teamId } = useSelectedTeam();
  const dispatch = useDispatch();
  const enabledCriteria =
    queryKey === "availableTags"
      ? !is.empty(teamId)
      : !is.empty(teamId) && !is.empty(id);
  return useQuery(queryKeys[queryKey](teamId, id), fetchWithError, {
    enabled: enabledCriteria,
    select: selector,
    onError: (error) => {
      const statusCode = error?.response?.status || 500;
      const message =
        statusCode === 500
          ? error?.response?.data?.message ||
            "Something went wrong, please try again later."
          : error?.response?.data?.message || "Unexpected error occurred.";

      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          severity: statusCode === 500 ? "error" : "warning",
          message,
        })
      );
    },
  });
};

export const useGetSettlementPercentage = (isCreateTeam) => {
  return useQuery(queryKeys["settlementPercentage"], fetchWithError, {
    enabled: !!isCreateTeam,
    select: selector,
  });
};

export const useGetPaymentDetailsForPriceGroup = (
  isCreatePriceGroup,
  teamId
) => {
  return useQuery(
    queryKeys["paymentDetailsForPriceGroup"](teamId),
    fetchWithError,
    {
      enabled: !!isCreatePriceGroup,
      select: selector,
    }
  );
};
export const usePaymentDetailsOfCompany = (hasFinanceConfigAccess) => {
  const { teamId } = useSelectedTeam();

  return useQuery(
    queryKeys["paymentDetailsOfCompany"](teamId),
    fetchWithError,
    {
      enabled: !!hasFinanceConfigAccess,
      select: selector,
    }
  );
};

export const useGetPaymentGatewayList = () => {
  const mutationResult = useMutation(fetchApi);
  return mutationResult;
};

export const useGetMeterValues = (
  queryKey,
  { showMeterValues, transactionId, connectorId } = {}
) => {
  const { teamId } = useSelectedTeam();
  return useQuery(
    queryKeys[queryKey](teamId, transactionId, connectorId),
    fetchWithError,
    {
      enabled: showMeterValues && !is.empty(teamId) && !is.empty(transactionId),
      select: selector,
    }
  );
};
export const useMutate = (queryKey, { id } = {}, reset = true) => {
  const queryClient = useQueryClient();
  const { teamId } = useSelectedTeam();
  const dispatch = useDispatch();
  const mutationResult = useMutation(fetchApi, {
    onSuccess: (data) => {
      if (queryKey !== "ChargerVisibility" && queryKey !== "payoutBulkUpd") {
        queryClient.invalidateQueries(queryKeys[queryKey](teamId, id));
      }
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: data?.message,
          severity: "success",
        })
      );
    },
    onError: (error) => {
      let message = "";

      if (error?.response?.data)
        message =
          error?.response?.data?.message ??
          error?.response?.data?.status_message;
      else message = error.message;

      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message,
          severity: "error",
        })
      );
    },
    onSettled: () => {
      reset && mutationResult.reset();
    },
  });
  return mutationResult;
};

export const useFetchOnDemand = (queryKey, fetchParams = false, showAlert = true) => {
  const { teamId } = useSelectedTeam();
  const { id = 0 } = useParams();
  const dispatch = useDispatch();
  const fetchData = async (params) => {
    try {
      const data = await fetchWithError({
        queryKey: queryKeys[queryKey](fetchParams ? id : teamId, params),
      });
      return data.data;
    } catch (error) {
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: error?.response?.data?.status_message ?? error?.response?.data?.message ?? error?.message,
          severity: "error",
        })
      );
      throw error;
    }
  };

  const { mutate, data, isLoading, isError } = useMutation(fetchData, {
    onSuccess: () => {
      if (showAlert) {
        dispatch(
          alertSlice.actions.setAlert({
            open: true,
            message: "Data fetched successfully",
            severity: "success",
          })
        );
      }
    },
  });

  return { fetchData: mutate, mutate, data, isLoading, isError };
};

/**
 * Generic polling query hook.
 *
 * @param {Object} params
 * @param {string} params.queryKey - Unique query key
 * @param {boolean} params.enabled - Whether polling should start
 * @param {number} [params.intervalMs=3000] - Polling interval in ms
 * @param {Function} [params.stopWhen] - Function to determine if polling should stop
 * @param {boolean} [params.initialFetch=true] - Whether to fetch data initially
 * @param {Function} [params.onDataChange] - Callback when data changes
 * @param {boolean} [params.immediateStart=false] - Whether to start polling immediately when enabled
 * @param {boolean} [params.startPolling=false] - Whether to start continuous polling
 */
export function usePollingQuery({
  queryKey,
  enabled = false,
  intervalMs = 3000,
  stopWhen,
  once = false,
  initialFetch = true,
  onDataChange,
  immediateStart = false,
  startPolling = false,
}) {
  const { id = 0 } = useParams();
  const { teamId } = useSelectedTeam();
  
  // Memoize query options to ensure refetchInterval updates when startPolling changes
  const queryOptions = useMemo(() => ({
    queryKey: queryKeys[queryKey](id || teamId),
    queryFn: fetchWithError,
    enabled: enabled && initialFetch,
    refetchInterval: (query) => {
      if (!query) return false;
      if (once) return false;
      if (!startPolling) return false; // Only poll when startPolling is true
      
      const data = query?.state?.data || query?.data;
      if (!data) return intervalMs;
      
      const shouldStop = stopWhen?.(data);
      return shouldStop ? false : intervalMs;
    },
    refetchIntervalInBackground: false,
    staleTime: 1000, // Consider data stale after 1 second
    cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1, // Limit retries to prevent excessive calls
    retryDelay: 1000, // Wait 1 second before retetch
  }), [queryKey, id, teamId, enabled, initialFetch, once, startPolling, intervalMs, stopWhen]);
  
  const queryResult = useQuery(queryOptions);

  // Call onDataChange when data changes - use useCallback to prevent infinite re-renders
  const stableOnDataChange = useCallback((data) => {
    if (onDataChange) {
      onDataChange(data);
    }
  }, [onDataChange]);

  useEffect(() => {
    if (stableOnDataChange && queryResult.data) {
      stableOnDataChange(queryResult.data);
    }
  }, [queryResult.data, stableOnDataChange]);

  // Start polling immediately when enabled changes - with debounce
  useEffect(() => {
    if (enabled && immediateStart && queryResult.refetch) {
      const timer = setTimeout(() => {
        queryResult.refetch();
      }, 100); // Small delay to prevent immediate refetch
      
      return () => clearTimeout(timer);
    }
  }, [enabled, immediateStart, queryResult.refetch]);



  return queryResult;
}
